import { useEffect, useRef, useState, type RefObject } from "react";
import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { toPng } from "html-to-image";
import { Download, Eye, Instagram, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

import type {
  CardSize,
  CardLang,
  CardTemplate,
  StrengthCardGeneratorProps,
  CardCanvasProps,
} from "./types";
import { CARD_SIZES, TEMPLATE_META, PREVIEW_SCALE, DEFAULT_PLATFORM_URL } from "./constants";
import { computeTopDomains } from "./styleHelpers";
import InstagramCardLTR from "./InstagramCardLTR";
import InstagramCardRTL from "./InstagramCardRTL";
import LinkedInCardLTR from "./LinkedInCardLTR";
import LinkedInCardRTL from "./LinkedInCardRTL";

// Map [size, isRTL] → card canvas component
const CARD_COMPONENTS: Record<CardSize, Record<"ltr" | "rtl", ComponentType<CardCanvasProps>>> = {
  instagram: { ltr: InstagramCardLTR, rtl: InstagramCardRTL },
  linkedin:  { ltr: LinkedInCardLTR,  rtl: LinkedInCardRTL  },
};

export default function StrengthCardGenerator({
  userName,
  userNameAr,
  strengths,
  topDomains: topDomainsProp,
  platformUrl = DEFAULT_PLATFORM_URL,
  userId,
}: StrengthCardGeneratorProps) {
  const { t, i18n } = useTranslation();
  const { toast }   = useToast();

  const [size,          setSize]          = useState<CardSize>("instagram");
  const [lang,          setLang]          = useState<CardLang>(i18n.language === "ar" ? "ar" : "en");
  const [template,      setTemplate]      = useState<CardTemplate>("bold");
  const [isDownloading, setIsDownloading] = useState(false);

  const cardRef  = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const cardDim  = CARD_SIZES[size];
  const isRTL    = lang === "ar";
  const displayName = isRTL ? (userNameAr ?? userName) : userName;

  // Use pre-fetched domains from parent; fall back to client-side computation.
  const topDomains = topDomainsProp ?? computeTopDomains(strengths);

  // Construct public profile URL for QR code
  // Get base URL from environment (frontend URL), fallback to current origin
  const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  const profileUrl = userId ? `${baseUrl}/profile/${userId}/strengths` : platformUrl;

  const CardComponent = CARD_COMPONENTS[size][isRTL ? "rtl" : "ltr"];
  const [previewScale, setPreviewScale] = useState(PREVIEW_SCALE);

  useEffect(() => {
    const preview = previewRef.current;
    if (!preview) return;

    const updatePreviewScale = () => {
      const availableWidth = Math.max(0, preview.clientWidth - 24);
      setPreviewScale(Math.min(PREVIEW_SCALE, availableWidth / cardDim.width));
    };

    updatePreviewScale();
    const observer = new ResizeObserver(updatePreviewScale);
    observer.observe(preview);
    return () => observer.disconnect();
  }, [cardDim.width]);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setIsDownloading(true);
    try {
      // Small delay to allow RTL text and fonts to fully render
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const dataUrl = await toPng(cardRef.current, {
        width: cardDim.width,
        height: cardDim.height,
        pixelRatio: 2, // Higher pixel ratio for better quality
        style: { transform: "none" },
        skipFonts: false, // Allow font processing for proper RTL rendering
        cacheBust: false, // Disable cache busting for better performance
        preferredFontFormat: 'woff2', // Prefer modern font format
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `strengths-card-${template}-${lang}-${size}-${Date.now()}.png`;
      link.click();
      toast({
        title: t("strengthCard.downloaded"),
        description: `${cardDim.width}×${cardDim.height}px PNG`,
      });
    } catch (err) {
      console.error("PNG export failed:", err);
      toast({ title: t("strengthCard.downloadError"), variant: "destructive" });
    } finally {
      setIsDownloading(false);
    }
  };

  if (strengths.length === 0) {
    return (
      <Card className="p-8 text-center text-muted-foreground">
        {t("strengthCard.noData")}
      </Card>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      {/* ── Controls ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        {/* Size selector */}
        <div className="flex w-full flex-col gap-2 min-[420px]:flex-row sm:w-auto">
          {(Object.entries(CARD_SIZES) as [CardSize, (typeof CARD_SIZES)[CardSize]][]).map(([key]) => (
            <Button
              key={key}
              variant={size === key ? "default" : "outline"}
              onClick={() => setSize(key)}
              className="flex-1 gap-2 sm:flex-none"
              size="sm"
            >
              {key === "instagram" ? <Instagram className="w-4 h-4" /> : <Linkedin className="w-4 h-4" />}
              {key === "instagram" ? "Instagram / WhatsApp" : "LinkedIn"}
            </Button>
          ))}
        </div>

        {/* Language selector */}
        <div className="flex w-full gap-2 min-[420px]:w-auto">
          <Button className="flex-1 min-[420px]:flex-none" variant={lang === "en" ? "default" : "outline"} onClick={() => setLang("en")} size="sm">
            🇬🇧 English
          </Button>
          <Button className="flex-1 min-[420px]:flex-none" variant={lang === "ar" ? "default" : "outline"} onClick={() => setLang("ar")} size="sm">
            🇸🇦 العربية
          </Button>
        </div>

        {/* Download */}
        <Button onClick={handleDownload} disabled={isDownloading} className="w-full gap-2 sm:w-auto">
          <Download className="w-4 h-4" />
          {isDownloading ? t("strengthCard.generating") : t("strengthCard.downloadPng")}
        </Button>
      </div>

      {/* ── Template selector ── */}
      <div className="flex flex-col gap-2">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
          Template
        </div>
        <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-3 sm:gap-3">
          {(Object.entries(TEMPLATE_META) as [CardTemplate, { label: string; desc: string }][]).map(
            ([key, meta]) => (
              <button
                key={key}
                onClick={() => setTemplate(key)}
                className={`flex min-w-0 flex-col items-start px-4 py-3 rounded-xl border text-start transition-all ${
                  template === key
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background/50 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                }`}
              >
                <span className="text-sm font-semibold">{meta.label}</span>
                <span className="text-xs opacity-70 mt-0.5">{meta.desc}</span>
              </button>
            )
          )}
        </div>
      </div>

      {/* Size hint */}
      <div className="flex min-w-0 items-start gap-2 text-sm text-muted-foreground">
        <Eye className="mt-0.5 h-4 w-4 shrink-0" />
        <span className="min-w-0 break-words">
          {cardDim.label} — {t("strengthCard.previewNote")}
        </span>
      </div>

      {/* ── Card preview ── */}
      <div
        ref={previewRef}
        className="flex w-full max-w-full items-start justify-center overflow-hidden rounded-xl border border-border bg-black/20 p-3 sm:p-4"
        style={{ minHeight: cardDim.height * previewScale + 24 }}
      >
        <div
          style={{
            width: cardDim.width * previewScale,
            height: cardDim.height * previewScale,
            flexShrink: 0,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              width: cardDim.width,
              height: cardDim.height,
              position: "absolute",
              left: "50%",
              transform: `translateX(-50%) scale(${previewScale})`,
              transformOrigin: "top center",
            }}
          >
            <CardComponent
              cardRef={cardRef as RefObject<HTMLDivElement>}
              userName={displayName}
              strengths={strengths}
              topDomains={topDomains}
              platformUrl={platformUrl}
              profileUrl={profileUrl}
              template={template}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
